import { computed, ComputedRef, nextTick, Ref, ref, watch } from 'vue';
import $t from '@Ravnur-Inc/l10n/$t';
import { Player$CombineOptions } from '@Ravnur-Inc/player/src/types/Options';
import {
  PLAYER_BASE_OPTIONS,
  SinglePlayableMedia,
  Timedata,
  TimedataSegment,
  TimedataState,
  TimedataThumbnail,
  TimedataType,
} from '@Ravnur-Inc/shared/types/Media';
import { Language } from '@Ravnur-Inc/shared/types/Language';
import { cloneDeep, isEqual } from 'lodash';
import is404 from '@Ravnur-Inc/core/transformers/error/is404';
import uploaderHelper from '@Ravnur-Inc/shared/helpers/fileuploader.helpers';
import Player from '@Ravnur-Inc/core/components/player/player';
import FileUploader from '@Ravnur-Inc/shared/components/file-uploader/file-uploader';
import { showDoneNotification, showErrorNotification } from '@Ravnur-Inc/notifications/service';
import { Player$State } from '@Ravnur-Inc/player/src/types/State';
import {
  Player$Source,
  Player$SourceStatus,
  Player$SourceType,
} from '@Ravnur-Inc/player/src/types/Source';
import { wait } from '@Ravnur-Inc/http';
import isMobile from '@Ravnur-Inc/helpers/featured.detect/isMobile';

export const EDITOR_TIME_FORMAT = 'HH:mm:ss.SS';
export const SEGMENT_SCROLL_OFFSET = 1;
export const STATE_REFRESHING_DEBOUNCE_TIME = 250;
export enum TimedataAbbreviation {
  CAPTION = 'cc',
  CUE_POINT = 'cp',
}

const ENG_LANG_CODE = 'en-US';
const MAX_FILE_SIZE = 1024 * 1024;
const VTT_TYPE: Player$SourceType = 'vtt';
const ALLOWED_FORMAT = VTT_TYPE;
const APPROVED_TIMEDATA_STATUS: Player$SourceStatus = 2;

enum TIMECODE_STATE_ACTION {
  APPROVE = 'approve',
  DISABLED = 'disable',
}

const TIMEDATA_MAP = {
  [TimedataAbbreviation.CAPTION]: TimedataType.CAPTIONS,
  [TimedataAbbreviation.CUE_POINT]: TimedataType.CUE_POINT,
};

export type BaseTimedataRepository = {
  getTimedata: (mediaId: string) => Promise<Timedata[]>;
  getTimedataByLanguage: (
    mediaId: string,
    language: string,
    timedataAbr: TimedataAbbreviation
  ) => Promise<Timedata>;
  getTranscriptByLanguage: (mediaId: string, language: string) => Promise<string>;
  updateTimedata: (
    mediaId: string,
    language: string,
    segments: TimedataSegment[],
    timedataAbr: TimedataAbbreviation
  ) => void;
  toggleTimedataState: (
    mediaId: string,
    language: string,
    state: string,
    timedataAbr: TimedataAbbreviation
  ) => void;
  deleteTimedata: (mediaId: string, language: string, timedataAbr: TimedataAbbreviation) => void;
  getThumbnailsFromPreview(previewUrl: string): Promise<TimedataThumbnail[]>;
};

export const useTimedataEditor = (
  timedataType: TimedataAbbreviation,
  repository: BaseTimedataRepository
) => {
  // template ref
  const player = ref<Nullable<Player>>(null);

  const currentMedia = ref<Nullable<SinglePlayableMedia>>(null);
  const languages = ref<Language[]>([]);

  const loading = ref(false);
  const selectedTimedata: Ref<Nullable<Timedata>> = ref(null);
  const originTimedata: Ref<Nullable<Timedata>> = ref(null);
  const timedata: Ref<Timedata[]> = ref([]);
  const allTimedata: Ref<Timedata[]> = ref([]);
  const activeSegment = ref(0);
  const searchString = ref('');
  const isPublished = ref(false);
  const newLanguageId = ref('');
  const matchIndex = ref(0);
  const selectedTranscript = ref('');
  const playerState: Ref<Nullable<Player$State>> = ref(null);
  const previewThubmnails: Ref<TimedataThumbnail[]> = ref([]);

  const playerPreview: ComputedRef<string> = computed(
    () => (player.value?.sources as Player$Source)?.preview || ''
  );

  const playerOptions: ComputedRef<Partial<Player$CombineOptions>> = computed(() => {
    return {
      ...PLAYER_BASE_OPTIONS,
      savePlayTime: false,
    };
  });

  const filteredPlayerState: ComputedRef<Nullable<Player$State>> = computed(() => {
    return playerState.value
      ? {
          ...playerState.value,
          cc: {
            ...playerState.value.cc,
            sources: playerState.value.cc.sources.filter((s) => !!s.srclang),
          },
        }
      : null;
  });

  const isChanged = computed(() => {
    if (!originTimedata.value || !selectedTimedata.value) {
      return;
    }

    return !isEqual(originTimedata.value, selectedTimedata.value);
  });

  const matchIndexes: ComputedRef<number[]> = computed(() => {
    if (!selectedTimedata.value || !selectedTimedata.value.segments) {
      return [];
    }

    const indexes: number[] = [];

    const search = searchString.value.toLowerCase();
    selectedTimedata.value.segments.forEach((s, idx) => {
      if (s.text.toLowerCase().indexOf(search) > -1) {
        indexes.push(idx);
      }
    });

    matchIndex.value = indexes[0];

    return indexes;
  });

  const languageOptions = computed(() => {
    return languages.value.map((l) => ({
      label: l.localName,
      code: l.abbreviation,
    }));
  });

  const existLangCodes = computed(() => {
    return timedata.value.map((t) => t.lang);
  });

  const pushState = (state: Player$State) => {
    if (player.value) {
      player.value.pushState(state);
    }
  };

  const onUpdateMatchIndex = (idx: number) => {
    matchIndex.value = idx;
  };

  const onSearchStringChange = (value: string) => {
    searchString.value = value;
  };

  const onIsPublishedChanged = async (value: boolean) => {
    isPublished.value = value;
    await togglePublishState();

    const selectedLanguage = timedata.value.find((t) => t.lang === selectedTimedata.value?.lang);

    if (selectedLanguage) {
      selectedLanguage.state = value ? TimedataState.AVAILABLE : TimedataState.BLOCKED;
    }
  };

  const discardChanges = () => {
    selectedTimedata.value = cloneDeep(originTimedata.value);
  };

  const setActiveSegment = (row: number) => {
    activeSegment.value = row;
    player.value?.setCurrentTime(selectedTimedata.value?.segments?.[row].from || 0);

    nextTick(() => {
      if (selectedTimedata.value) {
        updateLocalPlayerState(selectedTimedata.value);
        onSelectedCaptionChange(selectedTimedata.value);
      }
    });
  };

  const onSelectedCaptionChange = (timedata: Nullable<Timedata>) => {
    const state = player.value?.state;
    const clonedTimedata = cloneDeep(timedata);

    if (!state || !clonedTimedata?.segments) {
      return;
    }

    let update: Partial<Player$State> = {};

    if (timedataType === TimedataAbbreviation.CAPTION) {
      update = {
        cc: {
          ...state.cc,
          timedata: clonedTimedata.segments,
        },
      };
    }

    if (timedataType === TimedataAbbreviation.CUE_POINT) {
      update = {
        toc: {
          ...(state as any).toc,
          timedata: clonedTimedata.segments,
        },
      };
    }

    pushState({
      ...state,
      ...update,
    });
  };

  const handleTimecodeChange = (value: number, row: number, col: 'from' | 'to') => {
    if (!selectedTimedata.value || !selectedTimedata.value.segments) return;

    selectedTimedata.value.segments[row][col] = value;
  };

  const handleTextChange = (text: string, row: number) => {
    if (!selectedTimedata.value || !selectedTimedata.value.segments) return;

    selectedTimedata.value.segments[row].text = text;
  };

  const updateLocalPlayerState = (td: Timedata) => {
    const state = player.value?.state;
    const clonedTimedata = cloneDeep(td);

    if (!state || !clonedTimedata?.segments) {
      return;
    }

    let update: Partial<Player$State> = {};

    const updatedTimedata = {
      lang: clonedTimedata.lang,
      sources: timedata.value.map((t) => ({
        ...t,
        srclang: t.lang,
        src: '',
        type: VTT_TYPE,
        state: APPROVED_TIMEDATA_STATUS,
      })),
      timedata: clonedTimedata.segments,
      timedataLang: clonedTimedata.lang,
    };

    if (timedataType === TimedataAbbreviation.CAPTION) {
      update = {
        cc: {
          ...state.cc,
          ...updatedTimedata,
        },
      };
    }

    if (timedataType === TimedataAbbreviation.CUE_POINT) {
      update = {
        toc: {
          ...state.toc,
          ...updatedTimedata,
        },
      };
    }

    pushState({
      ...state,
      ...update,
    });
  };

  const onUploadTimedataFile = async (file: File, fileUploader: FileUploader) => {
    if (file.size > MAX_FILE_SIZE) {
      const maxSizeInMB = MAX_FILE_SIZE / (1024 * 1024);

      showErrorNotification(
        $t('video-editors', 'cc-editor__file-size-error', { size: `${maxSizeInMB} MB` })
      );

      return;
    }

    const info = fileUploader.createFileUploadInfo();

    if (!fileUploader || !info) {
      return;
    }

    if (info.data.fileExt !== ALLOWED_FORMAT) {
      showErrorNotification($t('video-editors', 'cc-editor__file-format-error'));
      return;
    }

    try {
      loading.value = true;

      const operation: Operation = await uploaderHelper.upload(info);
      await wait(operation);
      showDoneNotification($t('video-editors', 'editor__captions-file-uploaded'));
      await getAvailableTimedata();
      const newLanguage = timedata.value.find((c) => c.lang === newLanguageId.value);
      loadSelectedTimedata(newLanguage);
    } catch (e: any) {
      if ('errorReason' in e) {
        showErrorNotification(e.errorReason[0]);
      } else {
        showErrorNotification(e as string);
      }
      loading.value = false;
    }
  };

  const getAvailableTimedata = async () => {
    if (!currentMedia.value) {
      return;
    }

    loading.value = true;

    try {
      const response = await repository.getTimedata(currentMedia.value?.id);

      allTimedata.value = response;
      timedata.value = response.filter((t) => t.type === TIMEDATA_MAP[timedataType]);

      if (!timedata.value) return;

      const english = timedata.value.find((t) => t.lang === ENG_LANG_CODE);

      selectedTimedata.value = english ?? cloneDeep(timedata.value[0]) ?? null;
    } catch (e) {
      if (!is404(e)) {
        showErrorNotification(e as string);
      }
    } finally {
      loading.value = false;
    }
  };

  const deleteSegment = async (row: number) => {
    selectedTimedata.value?.segments?.splice(row, 1);
  };

  const loadSelectedTimedata = async (manualSelected: Nullable<Timedata> = null) => {
    if (!currentMedia.value || (!manualSelected && !selectedTimedata.value)) {
      return;
    }

    const caption = manualSelected || selectedTimedata.value;

    loading.value = true;

    try {
      isPublished.value = caption?.state === TimedataState.AVAILABLE;
      const response = await repository.getTimedataByLanguage(
        currentMedia.value?.id,
        caption?.lang || '',
        timedataType
      );

      if (timedataType === TimedataAbbreviation.CAPTION) {
        const transcript = await repository.getTranscriptByLanguage(
          currentMedia.value?.id,
          caption?.lang || ''
        );
        selectedTranscript.value = transcript;
      }

      selectedTimedata.value = response;
      originTimedata.value = cloneDeep(selectedTimedata.value);

      if (selectedTimedata.value) {
        updateLocalPlayerState(selectedTimedata.value);
      }
    } catch (e) {
      showErrorNotification(e as string);
    } finally {
      loading.value = false;
    }
  };

  const save = async () => {
    if (!currentMedia.value || !selectedTimedata.value || !selectedTimedata.value.segments) return;

    loading.value = true;

    try {
      const operation = await repository.updateTimedata(
        currentMedia.value?.id,
        selectedTimedata.value.lang,
        selectedTimedata.value.segments,
        timedataType
      );

      await wait(operation as any);
      await loadData(selectedTimedata.value);
    } catch (e: any) {
      if ('errorReason' in e) {
        showErrorNotification(e.errorReason[0]);
      } else {
        showErrorNotification(e as string);
      }
    } finally {
      loading.value = false;
    }
  };

  const loadData = async (manualSelected: Nullable<Timedata> = null) => {
    await getAvailableTimedata();
    await loadSelectedTimedata(manualSelected);
  };

  const togglePublishState = async () => {
    if (!currentMedia.value || !selectedTimedata.value) {
      return;
    }

    loading.value = true;

    try {
      const state = isPublished.value
        ? TIMECODE_STATE_ACTION.APPROVE
        : TIMECODE_STATE_ACTION.DISABLED;
      await repository.toggleTimedataState(
        currentMedia.value?.id,
        selectedTimedata.value.lang,
        state,
        timedataType
      );
    } catch (e) {
      showErrorNotification(e as string);
      isPublished.value = !isPublished.value;
    } finally {
      loading.value = false;
    }
  };

  const deleteTimedata = async () => {
    if (!currentMedia.value || !selectedTimedata.value) {
      return;
    }

    loading.value = true;

    try {
      await repository.deleteTimedata(
        currentMedia.value?.id,
        selectedTimedata.value.lang,
        timedataType
      );
      selectedTimedata.value = null;
      await loadData();
    } catch (e) {
      // this.$processException(e);
    } finally {
      loading.value = false;
    }
  };

  const changeSelectedTimedata = async (languageCode: string) => {
    const selected = timedata.value.find((c) => c.lang === languageCode);

    if (selected) {
      selectedTimedata.value = cloneDeep(selected);
    }

    await loadSelectedTimedata();
  };

  watch(playerPreview, async (preview) => {
    if (!preview || isMobile) return;

    previewThubmnails.value = await repository.getThumbnailsFromPreview(preview);
  });

  watch(() => selectedTimedata.value, onSelectedCaptionChange, {
    immediate: true,
    deep: true,
  });

  watch(
    () => currentMedia.value,
    () => loadData(),
    {
      immediate: true,
      deep: true,
    }
  );

  return {
    // data
    selectedTimedata,
    timedata,
    loading,
    isPublished,
    matchIndex,
    searchString,
    activeSegment,
    selectedTranscript,
    newLanguageId,
    playerState,
    currentMedia,
    languages,
    player,
    allTimedata,
    previewThubmnails,

    // getters
    playerOptions,
    isChanged,
    matchIndexes,
    languageOptions,
    existLangCodes,
    filteredPlayerState,

    // methods
    pushState,
    onUpdateMatchIndex,
    onSearchStringChange,
    discardChanges,
    setActiveSegment,
    handleTimecodeChange,
    handleTextChange,
    onUploadTimedataFile,
    deleteSegment,
    save,
    deleteTimedata,
    loadData,
    onIsPublishedChanged,
    getAvailableTimedata,
    loadSelectedTimedata,
    changeSelectedTimedata,
  };
};
